import {
  Checkbox,
  ConfirmationDialogBox,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { DownloadOutlineIcon } from '@atgfr8/icons';
import { setCustomerCommentVisiblityForComponent } from '@common/order/helperFunctions';
import { AutoComplete, BasicModal, Button, ToastType } from '@components';
import { createForm } from '@felte/solid';
import { checkIfDialogBoxIsOpen, closeDialogBox } from '@store/dialogBox';
import { closeModal, openModal } from '@store/modals';
import {
  ILoadComments,
  getCoordinates,
  orderStore,
  removeComment,
  sendComment,
} from '@store/orders';
import { ILocationSearch } from '@typeDefinitions/locationTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { DELETE_TRACKING_COMMENT_DIALOG_ID } from '@views/order/constants';
import { Show, createEffect, createSignal } from 'solid-js';

import { CustomerUpdateModal } from '../carrier/tracking/CustomerUpdateModal';
import { Comment } from './Comment';

const modalStyles = {
  minWidth: '900px',
  height: '500px',
  background: 'white',
  borderRadius: '5px',
};

type customerTrackingProps = {
  activeCarrierTab: number;
  addCommentChangeTrackingSource?: ChangeTrackingSource;
  deleteCommentChangeTrackingSource?: ChangeTrackingSource;
  hideTrackingComments: boolean;
  sendCustomerUpdateChangeTrackingSource?: ChangeTrackingSource;
  updateVisibilityChangeTrackingSource?: ChangeTrackingSource;
};

export type TrackingCommentSchema = {
  comment: string;
  loadId: number;
  state: string | null;
  city: string | null;
  latitude: string | null;
  longitude: string | null;
  temperature: number | null;
  visibleToCustomer: boolean;
  timestamp: string;
};

export const tempatureInitials = (unitOfTemperature: string | null) => {
  if (unitOfTemperature === 'Celsius') {
    return 'C';
  }
  return 'F';
};

export const CustomerTracking = (props: customerTrackingProps) => {
  const [comments, setComments] = createSignal<ILoadComments[] | undefined>();
  const [selectedComment, setSelectedComment] = createSignal<ILoadComments>();
  const [deleteRow, setDeleteRow] = createSignal<number | null>(null);
  const [query, setQuery] = createSignal<string>('');
  const [deletedComments, setDeletedComments] = createSignal<number[]>([]);

  const customerUpdateModalId = 'customerUpdateModal';

  const onCommentClick = (comment: ILoadComments) => {
    setSelectedComment(comment);
    openModal(customerUpdateModalId);
  };

  createEffect(() => {
    if (Boolean(orderStore.order.loads[props.activeCarrierTab]?.comments)) {
      setComments(orderStore.order.loads[props.activeCarrierTab]?.comments);
    }
  });

  createEffect(() => {
    if (props.hideTrackingComments) {
      setComments(
        orderStore.order.loads[props.activeCarrierTab]?.comments?.filter(
          (comment) => comment.name !== 'System Notification',
        ),
      );
    } else {
      setComments(orderStore.order.loads[props.activeCarrierTab]?.comments);
    }
  });

  const { form, errors, data, setData, reset } =
    createForm<TrackingCommentSchema>({
      initialValues: {
        comment: '',
        loadId: 0,
        state: null,
        city: null,
        latitude: null,
        longitude: null,
        temperature: null,
        visibleToCustomer: setCustomerCommentVisiblityForComponent(),
        timestamp: '',
      },
      onSubmit: async () => {
        if (orderStore.order.loads[props.activeCarrierTab]?.id == 0) {
          handleToast(
            ToastType.Caution,
            'Save the Load before adding comments',
          );
          return;
        }
        setData('loadId', orderStore.order.loads[props.activeCarrierTab]?.id);
        if (Boolean(data().city) && Boolean(data().state)) {
          const coords = await getCoordinates([
            {
              Address: {
                City: data().city as string,
                State: data().state as string,
              },
            },
          ]);
          if (coords[0]?.lat) {
            setData('latitude', coords[0].lat.toString());
            setData('longitude', coords[0].lng.toString());
          }
        }
        await sendComment(data(), props.addCommentChangeTrackingSource);
        setData('visibleToCustomer', setCustomerCommentVisiblityForComponent());
        reset();
      },
    });

  const handleLocationSelect = (item: ILocationSearch) => {
    setData('city', item.city);
    setData('state', item.state);
  };

  const handleDeleteComment = async () => {
    try {
      await removeComment(
        deleteRow()!,
        props.deleteCommentChangeTrackingSource,
      );
      setDeletedComments((prev) => [...prev, deleteRow()!]);
      setDeleteRow(null);

      handleToast(ToastType.Success, 'Comment deleted');
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  };

  createEffect(() => {
    if (orderStore.order.loads[props.activeCarrierTab].mode) {
      setData('visibleToCustomer', setCustomerCommentVisiblityForComponent());
    }
  });
  return (
    <>
      <form ref={form}>
        <Show
          when={
            orderStore.order.loads[orderStore.activeTab.index].mode !== 'LTL'
          }
        >
          <div class="text-[#123b50] font-normal text-base font-roboto flex mt-2">
            Tracking Calls
          </div>
        </Show>
        <div class="grid grid-cols-12 gap-1 mt-1 items-start">
          <div class="col-span-6">
            <AutoComplete<ILocationSearch>
              name="city"
              defaultValue=""
              endpoint="Location/Search/"
              version="v3"
              responseFieldName="display"
              onItemSelect={(item: ILocationSearch) => {
                setQuery(item.display);
                handleLocationSelect(item);
              }}
              id="autocomplete-field-location"
              label="Location"
              value={query()}
              onChange={(v) => {
                setQuery(v);
              }}
            />
          </div>
          <div class="col-span-3">
            <TextField
              label="Temp"
              name="temperature"
              onChange={(v: string) => {
                setData('temperature', Number(v));
              }}
              error={orderStore.isReadOnly ? undefined : errors().temperature}
              type="number"
              dataTestId={'temp-textInput'}
            />
          </div>
          <div class="col-span-1 ml-2">
            <Checkbox
              label="Visible to Customer"
              id="sendToCustomer"
              onChange={(v) => {
                setData('visibleToCustomer', v);
              }}
              checked={data().visibleToCustomer}
              dataTestId={'visible-to-customer-checkboxInput'}
              size="xs"
              color="info"
              class="ml-[-9px]"
            />
          </div>
          <div class="col-span-10">
            <TextField
              label="Comment"
              fullWidth={true}
              multiline
              rows={2}
              value={data().comment}
              onChange={(v: string) => {
                setData('comment', v);
              }}
              error={orderStore.isReadOnly ? undefined : errors().comment}
              dataTestId={'comment-textareafield'}
            />
          </div>
          <div class="col-span-1 self-center ml-1">
            <Button
              label="Save"
              variant="contained"
              type="submit"
              startIcon={<DownloadOutlineIcon size={20} />}
              disabled={orderStore.order.id === 0}
              color="primary"
              dataTestId={formatDataTestId('Save', 'button')}
            />
          </div>
        </div>
      </form>
      <div class="mt-2.5 overflow-y-auto max-h-[400px]">
        <table class="w-full">
          <tbody>
            {comments()?.map((comment) => (
              <Comment
                comment={comment}
                onCommentClick={onCommentClick}
                setDeleteRow={setDeleteRow}
                deletedComments={deletedComments()}
                setDeletedComments={setDeletedComments}
                changeTrackingSource={
                  props.updateVisibilityChangeTrackingSource
                }
              />
            ))}
          </tbody>
        </table>
        <BasicModal
          id={customerUpdateModalId}
          title="Send Customer Update"
          footer={false}
          onClose={() => {
            closeModal(customerUpdateModalId);
            setSelectedComment(undefined);
          }}
          modalStyles={modalStyles}
          width="820px"
          noCloseOnClickOutside={false}
        >
          <CustomerUpdateModal
            comment={selectedComment()}
            changeTrackingSource={props.sendCustomerUpdateChangeTrackingSource}
          />
        </BasicModal>
      </div>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(DELETE_TRACKING_COMMENT_DIALOG_ID)}
        dataTestId={DELETE_TRACKING_COMMENT_DIALOG_ID}
        id={DELETE_TRACKING_COMMENT_DIALOG_ID}
        title={'Are you sure you want to remove this comment?'}
        onSubmit={handleDeleteComment}
        onSubmitText={'Delete'}
        closeDialogBox={(_reason) => {
          setDeleteRow(null);
          closeDialogBox(DELETE_TRACKING_COMMENT_DIALOG_ID);
        }}
      />
    </>
  );
};
